import type { GamePhase, GameClass, ResourceType, StatType, StartingResources } from '../types';

export class GameState {
  // Resources
  money: number = 11;
  food: number = 0;
  motivation: number = 0;
  memory: number = 0;
  connections: number = 0;
  health: number = 4;
  badHabits: number = 9;
  sanity: number = 5;
  dignity: number = 0;
  
  private defaultStartingResources: StartingResources = {
    money: 11,
    food: 0,
    motivation: 0,
    memory: 0,
    connections: 0,
    health: 4,
    bad_habit: 9,
    sanity: 5,
    dignity: 0
  };
  
  // Stats
  smarts: number = 0;
  tenacity: number = 0;
  social: number = 0;
  
  // Game state
  phase: GamePhase = 'class_selection';
  selectedClass: GameClass | null = null;
  
  // Inspiration and thinking actions
  inspirationsUnlocked: number = 0;
  usedThinkingActions: Set<string> = new Set(); // Track one-time actions used
  
  // Health collapse tracking
  healthCollapsed: boolean = false;
  
  // Event handlers
  private onStateChange: (() => void) | null = null;
  
  setOnStateChange(handler: () => void): void {
    this.onStateChange = handler;
  }
  
  // Initialize starting resources from YAML
  initializeStartingResources(resources: StartingResources): void {
    this.defaultStartingResources = resources;
    this.money = resources.money;
    this.food = resources.food;
    this.motivation = resources.motivation;
    this.memory = resources.memory;
    this.connections = resources.connections;
    this.health = resources.health;
    this.badHabits = resources.bad_habit;
    this.sanity = resources.sanity;
    this.dignity = resources.dignity;
    this.notifyChange();
  }
  
  private notifyChange(): void {
    if (this.onStateChange) {
      this.onStateChange();
    }
  }
  
  // Resource getters
  getResource(resource: ResourceType): number {
    switch (resource) {
      case 'money': return this.money;
      case 'food': return this.food;
      case 'motivation': return this.motivation;
      case 'memory': return this.memory;
      case 'connections': return this.connections;
      case 'health': return this.health;
      case 'bad_habit': return this.badHabits;
      case 'sanity': return this.sanity;
      case 'dignity': return this.dignity;
    }
  }
  
  // Resource setters with validation
  setResource(resource: ResourceType, value: number): void {
    switch (resource) {
      case 'money': 
        this.money = Math.max(0, value);
        break;
      case 'food': 
        this.food = Math.max(0, value);
        break;
      case 'motivation': 
        this.motivation = Math.max(0, value);
        break;
      case 'memory': 
        this.memory = Math.max(0, value);
        break;
      case 'connections': 
        this.connections = Math.max(0, value);
        break;
      case 'health': 
        // Health can go negative, but when it reaches 0 or below, trigger special rule
        const previousHealth = this.health;
        this.health = value;
        if (this.health <= 0 && previousHealth > 0) {
          // Special rule: Health <= 0 -> Sanity -1, Health -> 1
          this.sanity = Math.max(0, this.sanity - 1);
          this.health = 1;
          this.healthCollapsed = true; // Flag for modal
        }
        break;
      case 'bad_habit': 
        this.badHabits = Math.max(0, value);
        break;
      case 'sanity': 
        this.sanity = Math.max(0, value);
        break;
      case 'dignity': 
        this.dignity = Math.max(0, value);
        break;
    }
    this.notifyChange();
  }
  
  modifyResource(resource: ResourceType, delta: number): void {
    const current = this.getResource(resource);
    this.setResource(resource, current + delta);
  }
  
  // Stat getters
  getStat(stat: StatType): number {
    switch (stat) {
      case 'smarts': return this.smarts;
      case 'tenacity': return this.tenacity;
      case 'social': return this.social;
    }
  }
  
  // Stat setters
  setStat(stat: StatType, value: number): void {
    switch (stat) {
      case 'smarts': 
        this.smarts = Math.max(0, value);
        break;
      case 'tenacity': 
        this.tenacity = Math.max(0, value);
        break;
      case 'social': 
        this.social = Math.max(0, value);
        break;
    }
    this.notifyChange();
  }
  
  modifyStat(stat: StatType, delta: number): void {
    const current = this.getStat(stat);
    this.setStat(stat, current + delta);
  }
  
  // Phase management
  setPhase(phase: GamePhase): void {
    this.phase = phase;
    this.notifyChange();
  }
  
  // Class selection
  selectClass(gameClass: GameClass): void {
    this.selectedClass = gameClass;
    
    // Apply starting skills
    this.setStat('smarts', gameClass.starting_skills.smarts);
    this.setStat('tenacity', gameClass.starting_skills.tenacity);
    this.setStat('social', gameClass.starting_skills.social);
    
    // Apply bonus resources
    const bonuses = gameClass.starting_bonus_resources;
    if (bonuses.money) this.modifyResource('money', bonuses.money);
    if (bonuses.food) this.modifyResource('food', bonuses.food);
    if (bonuses.motivation) this.modifyResource('motivation', bonuses.motivation);
    if (bonuses.memory) this.modifyResource('memory', bonuses.memory);
    if (bonuses.connections) this.modifyResource('connections', bonuses.connections);
    if (bonuses.health) this.modifyResource('health', bonuses.health);
    if (bonuses.bad_habit) this.modifyResource('bad_habit', bonuses.bad_habit);
    if (bonuses.sanity) this.modifyResource('sanity', bonuses.sanity);
    if (bonuses.dignity) this.modifyResource('dignity', bonuses.dignity);
    
    // Apply inspiration (special handling - not a regular resource)
    if (bonuses.inspiration) {
      for (let i = 0; i < bonuses.inspiration; i++) {
        this.unlockInspiration();
      }
    }
    
    this.setPhase('card_selection');
  }
  
  // Inspiration system
  unlockInspiration(): void {
    if (this.inspirationsUnlocked < 3) {
      this.inspirationsUnlocked++;
      this.notifyChange();
    }
  }
  
  getInspirationsUnlocked(): number {
    return this.inspirationsUnlocked;
  }
  
  markThinkingActionUsed(actionName: string): void {
    this.usedThinkingActions.add(actionName);
    this.notifyChange();
  }
  
  isThinkingActionUsed(actionName: string): boolean {
    return this.usedThinkingActions.has(actionName);
  }
  
  // Win/lose conditions
  checkWinCondition(): boolean {
    return this.dignity >= 6;
  }
  
  checkLoseCondition(): boolean {
    return this.sanity <= 0;
  }
  
  // Check and clear health collapse flag
  checkHealthCollapse(): boolean {
    if (this.healthCollapsed) {
      this.healthCollapsed = false;
      return true;
    }
    return false;
  }
  
  // Complete reset to initial state
  reset(): void {
    // Reset all resources to initial values from defaultStartingResources
    this.money = this.defaultStartingResources.money;
    this.food = this.defaultStartingResources.food;
    this.motivation = this.defaultStartingResources.motivation;
    this.memory = this.defaultStartingResources.memory;
    this.connections = this.defaultStartingResources.connections;
    this.health = this.defaultStartingResources.health;
    this.badHabits = this.defaultStartingResources.bad_habit;
    this.sanity = this.defaultStartingResources.sanity;
    this.dignity = this.defaultStartingResources.dignity;
    
    // Reset all stats
    this.smarts = 0;
    this.tenacity = 0;
    this.social = 0;
    
    // Reset game state
    this.phase = 'class_selection';
    this.selectedClass = null;
    
    // Reset inspiration and thinking actions
    this.inspirationsUnlocked = 0;
    this.usedThinkingActions.clear();
    
    // Reset health collapse flag
    this.healthCollapsed = false;
    
    // Notify change
    this.notifyChange();
  }
}

